import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { certificationsAPI, pdfCertificatesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Award, 
  Plus, 
  Calendar, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Edit2,
  Trash2,
  ExternalLink,
  Shield,
  FileText,
  Download,
  PenLine,
  FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const CERT_TYPES = [
  { value: 'GCP', label: 'GCP (Good Clinical Practice)', color: 'bg-blue-100 text-blue-700' },
  { value: 'HIPAA', label: 'HIPAA', color: 'bg-purple-100 text-purple-700' },
  { value: 'IATA', label: 'IATA (Dangerous Goods)', color: 'bg-amber-100 text-amber-700' },
];

const Certifications = () => {
  const { user, canManage } = useAuth();
  const [certifications, setCertifications] = useState([]);
  const [trainingCertificates, setTrainingCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signatureType, setSignatureType] = useState(null); // 'initial' or 'sop'
  const [signature, setSignature] = useState('');
  const [comments, setComments] = useState('');
  const [generating, setGenerating] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [certForm, setCertForm] = useState({
    cert_type: '',
    cert_name: '',
    issue_date: '',
    expiry_date: '',
    certificate_number: '',
    issuing_organization: '',
    document_url: '',
  });

  useEffect(() => {
    loadCertifications();
    loadTrainingCertificates();
  }, []);

  const loadCertifications = async () => {
    try {
      const response = await certificationsAPI.getMyCertifications();
      setCertifications(response.data);
    } catch (error) {
      console.error('Failed to load certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const loadTrainingCertificates = async () => {
    try {
      const response = await pdfCertificatesAPI.getMyCertificates();
      setTrainingCertificates(response.data.certificates || []);
    } catch (error) {
      console.error('Failed to load training certificates:', error);
    }
  };

  const handleGeneratePDF = async () => {
    if (!signature.trim()) {
      toast.error('Please enter your signature (full name)');
      return;
    }

    setGenerating(true);
    try {
      let response;
      if (signatureType === 'initial') {
        response = await pdfCertificatesAPI.generateInitialTrainingPDF(signature, comments);
      } else {
        response = await pdfCertificatesAPI.generateSOPTrainingPDF(signature, comments);
      }

      // Create a download link for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = signatureType === 'initial' 
        ? `Stryde_InitialTrainingRecord_${user.name.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}_Signed.pdf`
        : `Stryde_SOPTrainingRecord_${user.name.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}_Signed.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${signatureType === 'initial' ? 'Initial Training' : 'SOP Training'} Record PDF generated successfully!`);
      setShowSignModal(false);
      setSignature('');
      setComments('');
      loadTrainingCertificates();
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF certificate');
    } finally {
      setGenerating(false);
    }
  };

  const openSignModal = (type) => {
    setSignatureType(type);
    setSignature(user?.name || '');
    setComments('');
    setShowSignModal(true);
  };

  const handleSubmit = async () => {
    if (!certForm.cert_type || !certForm.cert_name || !certForm.issue_date || !certForm.expiry_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingCert) {
        await certificationsAPI.update(editingCert.id, certForm);
        toast.success('Certification updated');
      } else {
        await certificationsAPI.create({ ...certForm, user_id: user.id });
        toast.success('Certification added');
      }
      setShowAddModal(false);
      setEditingCert(null);
      resetForm();
      loadCertifications();
    } catch (error) {
      toast.error('Failed to save certification');
    }
  };

  const handleEdit = (cert) => {
    setEditingCert(cert);
    setCertForm({
      cert_type: cert.cert_type,
      cert_name: cert.cert_name,
      issue_date: cert.issue_date,
      expiry_date: cert.expiry_date,
      certificate_number: cert.certificate_number || '',
      issuing_organization: cert.issuing_organization || '',
      document_url: cert.document_url || '',
    });
    setShowAddModal(true);
  };

  const handleDelete = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) return;
    
    try {
      await certificationsAPI.delete(certId);
      toast.success('Certification deleted');
      loadCertifications();
    } catch (error) {
      toast.error('Failed to delete certification');
    }
  };

  const resetForm = () => {
    setCertForm({
      cert_type: '',
      cert_name: '',
      issue_date: '',
      expiry_date: '',
      certificate_number: '',
      issuing_organization: '',
      document_url: '',
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryBadge = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    
    if (days < 0) {
      return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Expired</Badge>;
    } else if (days <= 30) {
      return <Badge className="bg-red-100 text-red-700"><Clock className="w-3 h-3 mr-1" />{days} days left</Badge>;
    } else if (days <= 60) {
      return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />{days} days left</Badge>;
    } else if (days <= 90) {
      return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />{days} days left</Badge>;
    } else {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Valid</Badge>;
    }
  };

  const getCertTypeInfo = (type) => {
    return CERT_TYPES.find(t => t.value === type) || { label: type, color: 'bg-slate-100 text-slate-700' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            My Certifications
          </h1>
          <p className="text-slate-600 mt-1">
            Track your compliance certifications and generate training records
          </p>
        </div>
      </div>

      {/* Tabs for different certificate types */}
      <Tabs defaultValue="external" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-2">
          <TabsTrigger value="external" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            External Certifications
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Training Records
          </TabsTrigger>
        </TabsList>

        {/* External Certifications Tab */}
        <TabsContent value="external" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button 
              onClick={() => { resetForm(); setEditingCert(null); setShowAddModal(true); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-cert-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Certification
            </Button>
          </div>

          {certifications.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Award className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Certifications Yet</h3>
                <p className="text-slate-500 mb-4">Add your compliance certifications (GCP, HIPAA, IATA) to track expiry dates</p>
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Certification
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.map((cert) => {
                const typeInfo = getCertTypeInfo(cert.cert_type);
                const daysLeft = getDaysUntilExpiry(cert.expiry_date);
                
                return (
                  <Card 
                    key={cert.id} 
                    className={`border-slate-200 shadow-card hover:shadow-float transition-all ${
                      daysLeft <= 30 ? 'border-l-4 border-l-red-500' :
                      daysLeft <= 60 ? 'border-l-4 border-l-amber-500' : ''
                    }`}
                    data-testid={`cert-card-${cert.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <Badge className={typeInfo.color}>{cert.cert_type}</Badge>
                        {getExpiryBadge(cert.expiry_date)}
                      </div>
                      <CardTitle className="text-lg font-heading mt-2">{cert.cert_name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-slate-500">Issued</p>
                          <p className="font-medium">{new Date(cert.issue_date).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Expires</p>
                          <p className={`font-medium ${daysLeft <= 30 ? 'text-red-600' : ''}`}>
                            {new Date(cert.expiry_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      {cert.issuing_organization && (
                        <p className="text-sm text-slate-500">
                          Issued by: {cert.issuing_organization}
                        </p>
                      )}
                      
                      {cert.certificate_number && (
                        <p className="text-sm text-slate-500">
                          Cert #: {cert.certificate_number}
                        </p>
                      )}

                      <div className="flex gap-2 pt-2">
                        {cert.document_url && (
                          <a href={cert.document_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button variant="outline" size="sm" className="w-full">
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </a>
                        )}
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleEdit(cert)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(cert.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Training Records Tab */}
        <TabsContent value="training" className="mt-6">
          <div className="space-y-6">
            {/* PDF Generation Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Initial Training Record Card */}
              <Card className="border-slate-200 shadow-card hover:shadow-float transition-all">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FileCheck className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-heading">Initial Training Record</CardTitle>
                      <p className="text-sm text-slate-500">Mandatory trainings completion</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Generate a signed PDF record of your mandatory training completions including GCP, HIPAA, Company Handbook, and other required trainings.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <p className="font-medium text-slate-700 mb-1">Includes:</p>
                    <ul className="text-slate-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        GCP Training
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        HIPAA Training
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Company Handbook
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Role-specific Videos
                      </li>
                    </ul>
                  </div>
                  <Button 
                    onClick={() => openSignModal('initial')} 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="generate-initial-pdf-btn"
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Sign & Generate PDF
                  </Button>
                </CardContent>
              </Card>

              {/* SOP Training Record Card */}
              <Card className="border-slate-200 shadow-card hover:shadow-float transition-all">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-heading">SOP Training Record</CardTitle>
                      <p className="text-sm text-slate-500">SOP quiz completions</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Generate a signed PDF record of your SOP training completions with quiz pass/fail status for each Standard Operating Procedure.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <p className="font-medium text-slate-700 mb-1">Includes all SOPs:</p>
                    <ul className="text-slate-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        GA-01, GA-02 (General Admin)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        SM-02 to SM-12 (Study Management)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        WI-01 (Document Naming)
                      </li>
                    </ul>
                  </div>
                  <Button 
                    onClick={() => openSignModal('sop')} 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="generate-sop-pdf-btn"
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Sign & Generate PDF
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Previously Generated Certificates */}
            {trainingCertificates.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Download className="w-5 h-5 text-slate-600" />
                    Previously Generated Records
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {trainingCertificates.map((cert) => (
                      <div 
                        key={cert.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className={`w-5 h-5 ${cert.certificate_type === 'initial' ? 'text-blue-600' : 'text-purple-600'}`} />
                          <div>
                            <p className="font-medium text-slate-900">
                              {cert.certificate_type === 'initial' ? 'Initial Training Record' : 'SOP Training Record'}
                            </p>
                            <p className="text-sm text-slate-500">
                              Signed: {new Date(cert.signed_at).toLocaleDateString()} by {cert.signature}
                            </p>
                          </div>
                        </div>
                        <Badge className={cert.certificate_type === 'initial' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                          {cert.certificate_type === 'initial' ? `${cert.trainings_count || 0} trainings` : `${cert.sops_completed || 0} SOPs passed`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit External Certification Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCert ? 'Edit Certification' : 'Add Certification'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Certification Type *</Label>
              <Select 
                value={certForm.cert_type} 
                onValueChange={(v) => setCertForm({ ...certForm, cert_type: v })}
              >
                <SelectTrigger data-testid="cert-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {CERT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Certification Name *</Label>
              <Input
                value={certForm.cert_name}
                onChange={(e) => setCertForm({ ...certForm, cert_name: e.target.value })}
                placeholder="e.g., CITI GCP Certification"
                data-testid="cert-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Date *</Label>
                <Input
                  type="date"
                  value={certForm.issue_date}
                  onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })}
                  data-testid="cert-issue-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date *</Label>
                <Input
                  type="date"
                  value={certForm.expiry_date}
                  onChange={(e) => setCertForm({ ...certForm, expiry_date: e.target.value })}
                  data-testid="cert-expiry-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Certificate Number</Label>
              <Input
                value={certForm.certificate_number}
                onChange={(e) => setCertForm({ ...certForm, certificate_number: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="space-y-2">
              <Label>Issuing Organization</Label>
              <Input
                value={certForm.issuing_organization}
                onChange={(e) => setCertForm({ ...certForm, issuing_organization: e.target.value })}
                placeholder="e.g., CITI Program"
              />
            </div>

            <div className="space-y-2">
              <Label>Document URL</Label>
              <Input
                value={certForm.document_url}
                onChange={(e) => setCertForm({ ...certForm, document_url: e.target.value })}
                placeholder="Link to certificate PDF"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700" data-testid="save-cert-btn">
              {editingCert ? 'Update' : 'Add'} Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Modal for PDF Generation */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <PenLine className={`w-5 h-5 ${signatureType === 'initial' ? 'text-blue-600' : 'text-purple-600'}`} />
              Sign {signatureType === 'initial' ? 'Initial Training' : 'SOP Training'} Record
            </DialogTitle>
            <DialogDescription>
              By signing, you confirm that the training record information is correct.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Your Full Name (Signature) *</Label>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Enter your full name as signature"
                data-testid="signature-input"
                className="text-lg"
              />
              <p className="text-xs text-slate-500">This will appear as your digital signature on the PDF</p>
            </div>

            <div className="space-y-2">
              <Label>Comments (Optional)</Label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments for incomplete items..."
                rows={3}
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> The PDF will include your training completion dates. Incomplete trainings will be marked accordingly.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignModal(false)} disabled={generating}>
              Cancel
            </Button>
            <Button 
              onClick={handleGeneratePDF} 
              className={signatureType === 'initial' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}
              disabled={generating || !signature.trim()}
              data-testid="confirm-sign-btn"
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Sign & Download PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Certifications;
